import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, GraduationCap, Target, Zap } from 'lucide-react';

const CareerForm = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    stream: '',
    marks: '',
    interests: []
  });

  const [errors, setErrors] = useState({});

  const interestOptions = [
    'Technology', 'Healthcare', 'Business', 'Arts', 'Science', 
    'Engineering', 'Law', 'Media', 'Finance', 'Education',
    'Environment', 'Sports', 'Travel', 'Food', 'Fashion'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.stream) {
      newErrors.stream = 'Please select your stream';
    }

    if (!formData.marks) {
      newErrors.marks = 'Please enter your marks';
    } else if (isNaN(formData.marks) || formData.marks < 0 || formData.marks > 100) {
      newErrors.marks = 'Please enter valid marks between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="card"
      style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
        margin: '1rem 0'
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          marginBottom: '1rem' 
        }}>
          <Brain size={40} color="#667eea" />
        </div>
        <h2 style={{ 
          fontSize: '2rem', 
          fontWeight: '700', 
          color: '#333',
          marginBottom: '0.5rem'
        }}>
          AI Career Advisor
        </h2>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>
          Get personalized career recommendations based on your 12th marks and interests
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <label htmlFor="stream" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <GraduationCap size={20} />
            Academic Stream
          </label>
          <select
            id="stream"
            name="stream"
            value={formData.stream}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: `2px solid ${errors.stream ? '#ef4444' : '#e1e5e9'}`,
              borderRadius: '8px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease'
            }}
          >
            <option value="">Select your stream</option>
            <option value="science">Science (PCM/PCB)</option>
            <option value="commerce">Commerce</option>
            <option value="arts">Arts/Humanities</option>
          </select>
          {errors.stream && (
            <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              {errors.stream}
            </p>
          )}
        </div>

        <div className="input-group">
          <label htmlFor="marks" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Target size={20} />
            12th Marks (%)
          </label>
          <input
            type="number"
            id="marks"
            name="marks"
            value={formData.marks}
            onChange={handleInputChange}
            placeholder="Enter your percentage (e.g., 85)"
            min="0"
            max="100"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: `2px solid ${errors.marks ? '#ef4444' : '#e1e5e9'}`,
              borderRadius: '8px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease'
            }}
          />
          {errors.marks && (
            <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              {errors.marks}
            </p>
          )}
        </div>

        <div className="input-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Zap size={20} />
            Areas of Interest (Optional)
          </label>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '0.75rem' 
          }}>
            {interestOptions.map((interest) => (
              <button
                key={interest}
                type="button"
                onClick={() => handleInterestToggle(interest)}
                style={{
                  padding: '0.5rem 1rem',
                  border: `2px solid ${formData.interests.includes(interest) ? '#667eea' : '#e1e5e9'}`,
                  borderRadius: '8px',
                  background: formData.interests.includes(interest) ? '#667eea' : 'white',
                  color: formData.interests.includes(interest) ? 'white' : '#333',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                {interest}
              </button>
            ))}
          </div>
          <p style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Select up to 5 interests that match your preferences
          </p>
        </div>

        <motion.button
          type="submit"
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            width: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '16px 24px',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {loading ? (
            <>
              <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
              Analyzing...
            </>
          ) : (
            <>
              <Brain size={20} />
              Get Career Recommendations
            </>
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default CareerForm; 